import { useQuery } from '@apollo/client';
import { GET_FREE_TIME } from '@queries/getFreeTime.ts';
import AccountContext from 'context/AccountContext.tsx';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle.tsx';
import { useContext, useMemo } from 'react';
import {
  Query,
  QueryReadFreeTimeArgs,
  UnifiedPermit,
} from 'types/generatedSchemaTypes.ts';
import { ISO8601DurationShortened } from 'utils/timeUtils.ts';

const getLicensePlateForFreeTime = (permit?: UnifiedPermit, vehicles?: any) => {
  if (permit?.usageType === 'LICENSE_PLATE') {
    return permit?.registrationPlates?.map((plate) => plate?.plateText);
  }
  return vehicles?.map(
    (customerVehicle: any) => customerVehicle?.licensePlate?.plateText
  );
};

const getParkingZoneForFreeTime = (permit?: UnifiedPermit) => {
  // All parking zones in a sales location share the free time so we can take just the first one
  return (
    permit?.parkingZoneUid || permit?.salesLocation?.parkingZones?.[0] || ''
  );
};

const useGetLoyaltyPermitFreeTimes = (permit?: UnifiedPermit) => {
  const {
    customer: { vehicles },
  } = useContext(AccountContext);

  const permitRegistrationPlates = getLicensePlateForFreeTime(permit, vehicles);

  const { data, loading, error } = useQuery<Query, QueryReadFreeTimeArgs>(
    GET_FREE_TIME,
    {
      variables: {
        input: {
          parkingZoneUid: getParkingZoneForFreeTime(permit) as string,
          licensePlates: permitRegistrationPlates as string[],
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !permit,
    }
  );

  const registeredVehiclesWithFreeTimes = useMemo(() => {
    return (
      //Getting the vehicles that have the same license plate as the registered permit license plates
      (
        vehicles && permitRegistrationPlates
          ? vehicles?.filter((customerVehicle: any) =>
              permitRegistrationPlates?.find(
                (permitPlateText: any) =>
                  customerVehicle?.licensePlate?.plateText === permitPlateText
              )
            )
          : []
      )?.map((vehicle: any) => ({
        //Adding the parsed and shortened free time to the previously filtered vehicles
        ...vehicle,
        freeTime: ISO8601DurationShortened(
          data?.readFreeTime?.[vehicle.licensePlate?.plateText],
          ['days', 'hours', 'minutes']
        ),
      }))
    );
  }, [vehicles, data?.readFreeTime, permitRegistrationPlates]);

  const selectedVehicle = useSelectedParkingVehicle();
  const selectedVehicleWithFreeTime = useMemo(
    () =>
      registeredVehiclesWithFreeTimes?.find(
        (vehicle: any) =>
          vehicle?.licensePlate?.plateText ===
          selectedVehicle?.licensePlate?.plateText
      ),
    [selectedVehicle, registeredVehiclesWithFreeTimes]
  );

  return {
    data: data,
    loading: loading,
    error: error,
    registeredVehiclesWithFreeTime: registeredVehiclesWithFreeTimes,
    selectedVehicleWithFreeTime: selectedVehicleWithFreeTime,
  };
};

export default useGetLoyaltyPermitFreeTimes;
